import dayjs from "dayjs";
import { IAccount } from "~/types/account";
import { ILimit, IPlan, Limits, SubscriptionType } from "~/types/billing";
import { getTotalPlanCost } from "./numbers";

export const DISABLE_PAYMENTS = false;
export const DISABLE_TRIAL_FEES = true;

/**
 * Whether the account is inside a trial that has not run out yet.
 *
 * `has_subscribed_before` is true throughout a trial, so it cannot stand in for
 * "has paid us". Anything that turns on whether the user has actually been
 * charged has to ask this instead.
 */
export const isOnTrial = (account: IAccount | null): boolean => {
  const trialEndsAt = account?.active_subscription?.trial_end_at;

  return !!trialEndsAt && dayjs(trialEndsAt).isAfter(dayjs());
};

export const getCheckoutAction = (
  account: IAccount | null,
  plan: IPlan | undefined
): "trial" | "downgrade" | "upgrade" | "firstSubscription" => {
  const isDowngrade =
    account?.active_subscription?.plan &&
    getTotalPlanCost(plan) < getTotalPlanCost(account.active_subscription.plan);

  // Someone still inside their trial has not bought anything yet, so changing
  // plan is part of choosing one - not an upgrade or downgrade of something
  // they are paying for. `has_subscribed_before` is true during a trial, so on
  // its own it labelled the button "Upgrade to <plan>" while the merchant owed
  // nothing.
  if (!account?.has_subscribed_before || isOnTrial(account)) {
    if (!!plan?.trial_days) {
      return "trial";
    }

    return "firstSubscription";
  }

  if (isDowngrade) {
    return "downgrade";
  }

  return "upgrade";
};

export const isFeatureEnabledOnPlan = (
  limitType: Limits,
  plan: IPlan | undefined
) => {
  if (DISABLE_PAYMENTS) return true;

  const limits: ILimit | Record<any, any> = plan?.limits || {};

  if (limitType === Limits.PAID_PLAN) {
    return !!plan;
  }

  if (!limits) return false;

  return !!limits[limitType];
};
